import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import type { EntityStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { FeaturedMember, Group, GroupMembersEdge, User, YammerState } from '../../state/types';
import { createFindById, createFindByIdThrows } from '../findById';
import type { FindById, FindByIdThrows } from '../findById';
import { getFulfilledUsers } from '../users/selectors';

/**
 * @deprecated PERFORMANCE This returns an entity object and will force re-renders whenever an API call etc. updates the entity.
 * This should be replaced with a `getGroupMemberPanelField` selector.
 */
export const findById: FindById<'groupMemberPanels'> = createFindById('groupMemberPanels');
/**
 * @deprecated PERFORMANCE This returns an entity object and will force re-renders whenever an API call etc. updates the entity.
 * This should be replaced with a `getGroupMemberPanelFieldThrows` selector.
 */
export const findByIdThrows: FindByIdThrows<'groupMemberPanels'> = createFindByIdThrows('groupMemberPanels');

type GetStatusForId = (state: YammerState, id: Group['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, id) => getStatus(state.groupMemberPanels, id);

type IsLoaded = (state: YammerState, id: Group['id']) => boolean;
export const isLoaded: IsLoaded = (state, id) => getStatusForId(state, id) === 'fulfilled';

type GetHasMoreMembersOnServer = (state: YammerState, id: Group['id']) => boolean;
export const hasMoreMembersOnServer: GetHasMoreMembersOnServer = (state, id) => {
  const groupMembers = findById(state, id);

  return groupMembers ? groupMembers.hasMoreMembers : true;
};

type GetLastCachedGroupMemberEdge = (state: YammerState, id: Group['id']) => GroupMembersEdge | undefined;
export const getLastCachedGroupMemberEdge: GetLastCachedGroupMemberEdge = createCachedSelector(
  findById,
  (groupMembers) => (groupMembers?.members ? groupMembers.members.slice(-1)[0] : undefined)
)((_state, id) => id);

/**
 * @deprecated PERFORMANCE This dynamically generates an array of User entity objects with an extra `isAdmin` field. This will
 * return a new array instance every time the requested GroupMemberPanel entity is updated and every time any selected User entity
 * is updated and will force unnecessary re-renders. The consuming component should only select a memoized list of user ids, then
 * pass each id to a child component which would be responsible for selecting the specific User fields it requires and managing
 * its own memoization to avoid unnecessary re-renders.
 */
type GetGroupMembers = (state: YammerState, id: Group['id']) => FeaturedMember[];
export const getGroupMembers: GetGroupMembers = createCachedSelector(
  findByIdThrows,
  getFulfilledUsers,
  (groupMembers, users) =>
    groupMembers.members.reduce<FeaturedMember[]>((members, edge) => {
      const user = users[edge.nodeId];
      if (user) {
        members.push({
          ...user,
          isAdmin: edge.isAdmin,
        });
      }

      return members;
    }, [])
)((_state, id) => id);

type GetGroupAdminMemberIds = (state: YammerState, groupId: Group['id']) => User['id'][];
export const getGroupAdminMemberIds: GetGroupAdminMemberIds = createCachedSelector(
  findById,
  getFulfilledUsers,
  (groupMembers, users) => {
    if (!groupMembers) {
      return [];
    }

    return groupMembers.members.reduce<User['id'][]>((admins, edge) => {
      const user = users[edge.nodeId];
      if (user && edge.isAdmin) {
        admins.push(user.id);
      }

      return admins;
    }, []);
  }
)((_state, groupId) => groupId);
